'use client';

import { c as _c } from "react/compiler-runtime";
import './feature-article-card.scss';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';
import { titleCase } from '@sola/utils/string.ts';
import { Image } from '../../atoms/image/image.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC'
} as const;
type FeaturedArticleCardProps = SearchResultProps & {
  seoLevel: number;
};
export const FeaturedArticleCard = t0 => {
  const $ = _c(45);
  let article;
  let seoLevel;
  if ($[0] !== t0) {
    ({
      seoLevel,
      ...article
    } = t0);
    $[0] = t0;
    $[1] = article;
    $[2] = seoLevel;
  } else {
    article = $[1];
    seoLevel = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["articles", "geo"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    title,
    summary
  } = article;
  const href = article.path || "#";
  let t2;
  bb0: {
    if (!article.publishDate) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[4] !== article.publishDate) {
      t3 = new Date(article.publishDate).toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
      $[4] = article.publishDate;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const publishedDate = t2;
  const firstContributor = article.contributors?.[0] || null;
  const hasTags = !!(article.taxonomyArticleType || article.taxonomyProgramCountry?.length || article.taxonomyArticleTheme?.length || article.taxonomyTopic?.length);
  const isClickable = !!href && href !== "#";
  let t3;
  if ($[6] !== href || $[7] !== isClickable) {
    t3 = isClickable ? {
      Comp: Link as React.ElementType,
      CompProps: {
        href
      }
    } : {
      Comp: "div" as React.ElementType,
      CompProps: {}
    };
    $[6] = href;
    $[7] = isClickable;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    Comp,
    CompProps
  } = t3;
  const t4 = article.media?.image;
  let t5;
  if ($[9] !== t4) {
    t5 = <Image image={t4} className="FeaturedArticleCard__image" hasPlaceholder={true} />;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="FeaturedArticleCard__media-overlay" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5) {
    t7 = <div className="FeaturedArticleCard__media">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== summary) {
    t8 = <p>{summary}</p>;
    $[14] = summary;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== seoLevel || $[17] !== t8 || $[18] !== title) {
    t9 = <FeatureBase seoLevel={seoLevel} title={title} body={t8} className="max-w-md @lg:pr-roomy-lg" inline={true} />;
    $[16] = seoLevel;
    $[17] = t8;
    $[18] = title;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== firstContributor || $[21] !== t) {
    t10 = firstContributor && <div className="flex flex-col gap-snug-lg"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Potent}>{t("articles:writtenBy")}</Text><div className="flex flex-row items-center gap-snup-lg">{firstContributor.image?.url && <div className="pr-comfy-xs"><Image image={{
            url: firstContributor.image?.url,
            alt: firstContributor.image?.alt || firstContributor.name
          }} className="FeaturedArticleCard__avatar w-8 h-8 rounded-full object-cover" /></div>}<Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Moderate}>{firstContributor.name}</Text></div></div>;
    $[20] = firstContributor;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== publishedDate || $[24] !== t) {
    t11 = publishedDate && <div className="flex flex-col gap-snug-lg"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Potent}>{t("articles:publishedOn")}</Text><div className="mt-snug-lg"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Moderate}>{publishedDate}</Text></div></div>;
    $[23] = publishedDate;
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== article.taxonomyArticleTheme || $[27] !== article.taxonomyArticleType || $[28] !== article.taxonomyProgramCountry || $[29] !== article.taxonomyTopic || $[30] !== hasTags || $[31] !== t) {
    t12 = hasTags && <div className="flex flex-col gap-snug-lg sm:ml-auto text-left"><Text variant={Variants.Label} size={Sizes.Medium} prominence={Prominence.Potent}>{t("articles:tags")}</Text><div className="flex flex-wrap justify-end gap-snug-xl mt-snug-lg">{article.taxonomyArticleType?.[0] && <Badge key="type" label={titleCase(article.taxonomyArticleType)} />}{article.taxonomyProgramCountry?.[0] && <Badge key="country" label={titleCase(t(`geo:countries.${article.taxonomyProgramCountry[0]}.nameS`))} />}{article.taxonomyArticleTheme?.[0] && <Badge key="theme" label={titleCase(article.taxonomyArticleTheme[0])} />}{article.taxonomyTopic?.[0] && <Badge key="topic" label={titleCase(article.taxonomyTopic[0])} />}</div></div>;
    $[26] = article.taxonomyArticleTheme;
    $[27] = article.taxonomyArticleType;
    $[28] = article.taxonomyProgramCountry;
    $[29] = article.taxonomyTopic;
    $[30] = hasTags;
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12) {
    t13 = <div className="FeaturedArticleCard__meta flex items-start justify-between gap-comfy-xl flex-wrap pt-comfy-lg">{t10}{t11}{t12}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t13 || $[38] !== t9) {
    t14 = <div className="FeaturedArticleCard__content">{t9}{t13}</div>;
    $[37] = t13;
    $[38] = t9;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== Comp || $[41] !== CompProps || $[42] !== t14 || $[43] !== t7) {
    t15 = <Comp {...CompProps} className="FeaturedArticleCard w-full max-w-xl mx-auto aspect-ratio-1 @md:aspect-ratio-16-9 @lg:aspect-ratio-21-9">{t7}{t14}</Comp>;
    $[40] = Comp;
    $[41] = CompProps;
    $[42] = t14;
    $[43] = t7;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
};